import React, { useEffect, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Eye, Zap, Target, Brain, Crown, Theater, BookOpen, UserCheck, AlertTriangle } from 'lucide-react';
import { UrgencyBar } from '../components/UrgencyBar';
import { PurchasePopup } from '../components/PurchasePopup';
import { TestimonialCard } from '../components/TestimonialCard';
import { ComboCard } from '../components/ComboCard';
// Image Constants
const IMAGES = {
  MAIN_PRODUCT: 'https://cdn.magicpatterns.com/uploads/kYz1HhWECJVSPeFtyM737Y/ChatGPT_Image_27_ene_2026,_02_13_39.png',
  BONUS_1: 'https://cdn.magicpatterns.com/uploads/2gbH4DjCGn44jQsDErxfnV/ChatGPT_Image_27_ene_2026,_02_44_14.png',
  BONUS_2: 'https://cdn.magicpatterns.com/uploads/tmmSwC9h6AFBXSfzHkXd4T/ChatGPT_Image_27_ene_2026,_02_44_10.png',
  BONUS_3: 'https://cdn.magicpatterns.com/uploads/35g3YGsWzEsLy8oBUGfM3c/ChatGPT_Image_27_ene_2026,_02_44_04.png',
  MERCADO_LIBRE: 'https://cdn.magicpatterns.com/uploads/8r1qFw9uWhEVzEgJ6WLc17/Hotmart_logo.svg.png',
  COMBO_1: 'https://cdn.magicpatterns.com/uploads/s4MGB9kGsyNMzqDLKEUAnv/ChatGPT_Image_27_ene_2026,_02_13_39.png',
  COMBO_2: 'https://cdn.magicpatterns.com/uploads/mj5B8BvRdTCnLH5qA38vf2/ChatGPT_Image_27_ene_2026,_13_36_38.png',
  COMBO_3: 'https://cdn.magicpatterns.com/uploads/piYJqddWjF6TxsW87ub5g3/ChatGPT_Image_27_ene_2026,_13_45_30.png',
  PAYMENT_CHILE: 'https://cdn.magicpatterns.com/uploads/mLWT6391PT2Zzs8dhrTFRj/image.png',
  PAYMENT_COLOMBIA: 'https://cdn.magicpatterns.com/uploads/c4tU4K6Sa62U7LnTEyjUU3/image.png',
  PAYMENT_MEXICO: 'https://cdn.magicpatterns.com/uploads/b5UAjLzLDv9yXku17DUFQi/image.png'
};
// Checkout Links
const CHECKOUT_LINKS = {
  COMBO_1: 'https://pay.hotmart.com/E104311454M',
  COMBO_2: 'https://pay.hotmart.com/G104312498A',
  COMBO_3: 'https://pay.hotmart.com/A104312600R'
};
export function SalesPage() {
  // Meta Pixel Initialization
  useEffect(() => {
    document.title = 'MANUAL DE LENGUAJE CORPORAL';
    const PIXEL_ID = '888330477169466';
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window,document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${PIXEL_ID}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Descubre el sistema de 3 capas para leer personas, proyectar autoridad y defenderte de la manipulación.');
    }
    return () => {
      document.head.removeChild(script);
      document.body.removeChild(noscript);
    };
  }, []);
  const handleInitiateCheckout = (comboName: string, value: number) => {
    if (typeof (window as any).fbq === 'function') {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: comboName,
        value: value,
        currency: 'USD'
      });
    }
  };
  const scrollToCheckout = () => {
    const element = document.getElementById('checkout-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="bg-black min-h-screen text-white font-sans selection:bg-yellow-500 selection:text-black overflow-x-hidden" data-id="element-66">
      <UrgencyBar data-id="element-67" />
      <PurchasePopup data-id="element-68" />

      {/* SECTION 1: HERO */}
      <section className="relative pt-20 pb-32 px-4 overflow-hidden" data-id="element-69">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-zinc-800/50 via-black to-black z-0" data-id="element-70"></div>

        <div className="max-w-6xl mx-auto relative z-10 grid md:grid-cols-2 gap-12 items-center" data-id="element-71">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-center md:text-left" data-id="element-72">
            <h2 className="text-yellow-400 font-bold tracking-wider uppercase mb-4 text-sm md:text-base" data-id="element-73">
              Nadie Te Preparó Para Esto…
            </h2>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6" data-id="element-74">
              En el mundo real, no gana el que habla más. <br data-id="element-75" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-500 to-yellow-200" data-id="element-76">
                Gana el que entiende mejor.
              </span>
            </h1>
            <p className="text-gray-300 text-lg md:text-xl mb-8 leading-relaxed max-w-lg mx-auto md:mx-0" data-id="element-77">
              Descubre el sistema de 3 capas para leer personas, proyectar
              autoridad y defenderte de la manipulación. Usado por miles de
              personas en toda Latinoamérica para dominar sus relaciones,
              negocios y vida social.
            </p>

            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={scrollToCheckout} className="group bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-lg py-4 px-8 rounded-full shadow-[0_0_20px_rgba(234,179,8,0.5)] flex items-center gap-2 mx-auto md:mx-0 transition-all" data-id="element-78">
              Quiero Dominar Mi Entorno
              <ArrowDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" data-id="element-79" />
            </motion.button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-80">
            <div className="absolute inset-0 bg-yellow-500/20 blur-[100px] rounded-full" data-id="element-81"></div>
            <motion.img whileHover={{
            scale: 1.02,
            rotate: -1
          }} src={IMAGES.MAIN_PRODUCT} alt="Manual de Lenguaje Corporal" className="relative z-10 w-full max-w-md mx-auto drop-shadow-2xl" data-id="element-82" />
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: SALES LETTER */}
      <section className="bg-zinc-900/30 py-20 px-4 border-y border-zinc-800" data-id="element-83">
        <div className="max-w-3xl mx-auto prose prose-invert prose-lg" data-id="element-84">
          <p className="text-xl text-gray-300 leading-relaxed" data-id="element-85">
            Desde pequeño te enseñaron a hablar. A ser educado. A confiar. A ser
            correcto.
          </p>

          <blockquote className="border-l-4 border-yellow-500 pl-6 my-8 italic text-2xl text-white font-serif" data-id="element-86">
            "Pero nunca te enseñaron a leer personas."
          </blockquote>

          <p className="text-xl text-gray-300 font-medium mb-12" data-id="element-87">
            Y eso… lo pagaste caro.
          </p>

          <h3 className="text-3xl font-bold text-white mt-16 mb-6" data-id="element-88">
            El Problema No Eres Tú
          </h3>
          <p className="text-gray-400" data-id="element-89">
            Has intentado seguir los consejos de siempre. "Sé tú mismo", te
            dicen. Pero en reuniones importantes, citas o negociaciones, sientes
            que algo se te escapa. Ves cómo otros, quizás menos preparados que
            tú, se llevan el crédito, la atención o el "sí".
          </p>

          <h3 className="text-3xl font-bold text-white mt-16 mb-6" data-id="element-90">
            Intenté de Todo… Y Nada Funcionó
          </h3>
          <p className="text-gray-400 mb-8" data-id="element-91">
            Libros académicos aburridos, videos de YouTube contradictorios,
            consejos de "expertos" que nunca han negociado nada real. Hasta que
            entendí que el lenguaje corporal no es sobre "posturas", es sobre{' '}
            <strong data-id="element-92">estrategia</strong>.
          </p>

          <div className="bg-zinc-800/50 p-8 rounded-2xl border border-zinc-700 my-12" data-id="element-93">
            <h4 className="text-2xl font-bold text-yellow-400 mb-6 text-center" data-id="element-94">
              El Descubrimiento: El Sistema de 3 Capas
            </h4>
            <div className="flex flex-wrap justify-center gap-4 mb-6" data-id="element-95">
              <span className="bg-zinc-700 text-white px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide" data-id="element-96">
                1. Leer
              </span>
              <span className="bg-zinc-700 text-white px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide" data-id="element-97">
                2. Proyectar
              </span>
              <span className="bg-zinc-700 text-white px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide" data-id="element-98">
                3. Defenderte
              </span>
            </div>
            <p className="text-center text-gray-300 mb-6" data-id="element-99">
              Por eso nació este manual completo de lenguaje corporal. No es
              teoría, es un arsenal de herramientas para:
            </p>
            <ul className="space-y-3 text-gray-300" data-id="element-100">
              <li className="flex items-start gap-3" data-id="element-101">
                <CheckIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-102" />
                <span data-id="element-103">
                  Entender lo que la gente realmente piensa, no lo que dice.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-104">
                <CheckIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-105" />
                <span data-id="element-106">
                  Causar impacto en cualquier lugar sin forzar la atención.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-107">
                <CheckIcon className="w-6 h-6 text-green-500 flex-shrink-0" data-id="element-108" />
                <span data-id="element-109">
                  Detectar y neutralizar intentos de manipulación al instante.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 3: BENEFITS */}
      <section className="bg-black py-24 px-4" data-id="element-110">
        <div className="max-w-6xl mx-auto" data-id="element-111">
          <div className="text-center mb-16" data-id="element-112">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-113">
              Qué Va a Cambiar en Ti
            </h2>
            <div className="h-1 w-24 bg-yellow-500 mx-auto rounded-full" data-id="element-114"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16" data-id="element-115">
            <BenefitCard icon={<Eye className="w-8 h-8 text-yellow-400" data-id="element-117" />} title="Notar cosas antes" description="Verás micro-expresiones y señales que el 99% de la gente ignora por completo." data-id="element-116" />
            <BenefitCard icon={<Zap className="w-8 h-8 text-yellow-400" data-id="element-119" />} title="Dudar menos" description="Elimina la inseguridad social al saber exactamente qué está pasando en cada interacción." data-id="element-118" />
            <BenefitCard icon={<Target className="w-8 h-8 text-yellow-400" data-id="element-121" />} title="Elegir mejor" description="Detecta mentiras y verdades ocultas en negocios, trabajo y relaciones personales." data-id="element-120" />
            <BenefitCard icon={<Brain className="w-8 h-8 text-yellow-400" data-id="element-123" />} title="Decidir con claridad" description="Toma decisiones basadas en información real, no en palabras vacías." data-id="element-122" />
            <BenefitCard icon={<Crown className="w-8 h-8 text-yellow-400" data-id="element-125" />} title="Ser respetado" description="Proyecta una autoridad natural que hace que los demás quieran escucharte." data-id="element-124" />
            <BenefitCard icon={<Theater className="w-8 h-8 text-yellow-400" data-id="element-127" />} title="Sin fingir" description="Internaliza los principios para que tu carisma sea auténtico, no actuado." data-id="element-126" />
          </div>

          {/* Mid-page CTA */}
          <div className="text-center" data-id="element-128">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={scrollToCheckout} className="group bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-lg py-4 px-10 rounded-full shadow-[0_0_20px_rgba(234,179,8,0.5)] inline-flex items-center gap-2 transition-all" data-id="element-129">
              Quiero Empezar Ahora
              <ArrowDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" data-id="element-130" />
            </motion.button>
          </div>
        </div>
      </section>

      {/* SECTION 4: BONUS */}
      <section className="bg-zinc-900 py-24 px-4" data-id="element-131">
        <div className="max-w-6xl mx-auto" data-id="element-132">
          <div className="text-center mb-16" data-id="element-133">
            <span className="inline-block bg-red-600 text-white font-bold px-4 py-1 rounded-full text-sm uppercase tracking-wider mb-6 animate-pulse" data-id="element-134">
              Gratis por hoy
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-135">
              Tus 3 Bonus Exclusivos
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-136">
            <BonusCard image={IMAGES.BONUS_1} title="Lectura Rápida" subtitle="Descifra a cualquiera en segundos" description="Aprende los atajos mentales para perfilar personas apenas entras a un lugar." data-id="element-137" />
            <BonusCard image={IMAGES.BONUS_2} title="Influencia Invisible" subtitle="Persuasión subconsciente" description="Técnicas sutiles para guiar decisiones ajenas sin que se den cuenta." data-id="element-138" />
            <BonusCard image={IMAGES.BONUS_3} title="Detector de Mentiras" subtitle="Verdad vs Ficción" description="Las señales inconfundibles que delatan el engaño y la manipulación." data-id="element-139" />
          </div>
        </div>
      </section>

      {/* SECTION 5: CHECKOUT */}
      <section id="checkout-section" className="bg-black py-24 px-4 relative" data-id="element-140">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-900/10 via-black to-black pointer-events-none" data-id="element-141"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-142">
          <div className="text-center mb-16" data-id="element-143">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-144">
              Elige Tu Nivel de Dominio
            </h2>
            <p className="text-xl text-gray-400 mb-8" data-id="element-145">
              Descarga inmediata. Acceso de por vida.
            </p>

            <div className="inline-flex items-center gap-2 bg-red-900/30 border border-red-800 px-4 py-2 rounded-full" data-id="element-146">
              <span className="relative flex h-3 w-3" data-id="element-147">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-148"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" data-id="element-149"></span>
              </span>
              <span className="text-red-400 font-bold text-sm" data-id="element-150">
                Solo quedan 7 copias con estos bonus
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-end" data-id="element-151">
            {/* Combo 1 */}
            <ComboCard title="Lector Humano" price="13.90" originalPrice="19.90" priceUSD="" description="Lo esencial para empezar a leer tu entorno hoy mismo." features={['Manual de Lenguaje Corporal (PDF digital)', 'Bonus 1: Lectura Rápida (PDF digital)', 'Bonus 2: Influencia Invisible (PDF digital)', 'Bonus 3: Detector de Mentiras (PDF digital)']} checkoutUrl={CHECKOUT_LINKS.COMBO_1} onBuy={() => handleInitiateCheckout('Combo 1 - Lector Humano', 13.9)} image={IMAGES.COMBO_1} data-id="element-152" />

            {/* Combo 2 - Recommended */}
            <ComboCard title="Influencia Total" price="19.90" originalPrice="29.90" priceUSD="" description="Para quienes buscan resultados profesionales y liderazgo." features={['Todo lo del Combo Lector Humano', 'Manual de Carisma y Presencia (PDF digital)']} isRecommended={true} checkoutUrl={CHECKOUT_LINKS.COMBO_2} onBuy={() => handleInitiateCheckout('Combo 2 - Influencia Total', 19.9)} image={IMAGES.COMBO_2} data-id="element-153" />

            {/* Combo 3 */}
            <ComboCard title="Dominio Mental" price="26.90" originalPrice="39.90" priceUSD="" description="El arsenal completo para el control total de la interacción." features={['Todo lo del Combo Lector Humano', 'Todo lo del Combo Influencia Total', 'Manual de Psicología Oscura (PDF digital)']} isPremium={true} checkoutUrl={CHECKOUT_LINKS.COMBO_3} onBuy={() => handleInitiateCheckout('Combo 3 - Dominio Mental', 26.9)} image={IMAGES.COMBO_3} data-id="element-154" />
          </div>

          {/* Trust + Payment + Guarantee block */}
          <div className="mt-16 text-center" data-id="element-155">
            {/* Hotmart trust badge */}
            <p className="text-gray-500 text-sm uppercase tracking-widest mb-3" data-id="element-156">
              Pago 100% seguro procesado por
            </p>
            <div className="bg-white rounded-lg p-4 inline-block max-w-[240px] mb-8" data-id="element-157">
              <img src={IMAGES.MERCADO_LIBRE} alt="Hotmart" className="h-10 object-contain mx-auto" data-id="element-158" />
            </div>

            <p className="text-gray-400 text-base mb-6" data-id="element-159">
              💳 Al momento del pago puedes elegir pagar en{' '}
              <span className="text-white font-semibold" data-id="element-160">tu moneda local</span>.
              Aceptamos múltiples medios de pago:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-6" data-id="element-161">
              <div data-id="element-162">
                <p className="text-gray-500 text-xs uppercase tracking-widest mb-3 font-semibold" data-id="element-163">
                  🇲🇽 México
                </p>
                <div className="bg-white rounded-xl p-4 shadow-lg" data-id="element-164">
                  <img src={IMAGES.PAYMENT_MEXICO} alt="Medios de pago México" className="w-full object-contain" data-id="element-165" />
                </div>
              </div>
              <div data-id="element-166">
                <p className="text-gray-500 text-xs uppercase tracking-widest mb-3 font-semibold" data-id="element-167">
                  🇨🇱 Chile
                </p>
                <div className="bg-white rounded-xl p-3 shadow-lg" data-id="element-168">
                  <img src={IMAGES.PAYMENT_CHILE} alt="Medios de pago Chile" className="w-full object-contain" data-id="element-169" />
                </div>
              </div>
              <div data-id="element-170">
                <p className="text-gray-500 text-xs uppercase tracking-widest mb-3 font-semibold" data-id="element-171">
                  🇨🇴 Colombia
                </p>
                <div className="bg-white rounded-xl p-3 shadow-lg" data-id="element-172">
                  <img src={IMAGES.PAYMENT_COLOMBIA} alt="Medios de pago Colombia" className="w-full object-contain" data-id="element-173" />
                </div>
              </div>
            </div>

            <p className="text-gray-600 text-xs mt-4" data-id="element-174">
              Y muchos más métodos disponibles según tu país.
            </p>

            {/* Garantía 7 días */}
            <div className="mt-10 max-w-xl mx-auto bg-zinc-900 border border-zinc-800 rounded-2xl p-6" data-id="element-175">
              <div className="flex items-center justify-center gap-3 mb-3" data-id="element-176">
                <span className="text-3xl" data-id="element-177">🛡️</span>
                <h4 className="text-white font-bold text-lg" data-id="element-178">
                  Garantía de 7 Días
                </h4>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-179">
                Si por cualquier motivo no quedas satisfecho con tu compra,
                tienes <span className="text-white font-semibold" data-id="element-180">7 días</span>{' '}
                para solicitar un reembolso completo, sin preguntas y sin
                complicaciones. Tu inversión está 100% protegida.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: TESTIMONIALS (after checkout) */}
      <section className="bg-zinc-900 py-24 px-4" data-id="element-181">
        <div className="max-w-7xl mx-auto" data-id="element-182">
          <h2 className="text-4xl font-bold text-center mb-16" data-id="element-183">
            Lo que dicen quienes ya lo aplicaron
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-184">
            <TestimonialCard name="Carlos Ramírez" role="Vendedor, 34 años" content="Cerré tres negocios la semana pasada solo aplicando la técnica del 'espejo sutil'. La gente confía en mí mucho más rápido. Es como tener un superpoder en las negociaciones." data-id="element-185" />
            <TestimonialCard name="Fernanda López" role="Gerente de Recursos Humanos, 41 años" content="En entrevistas de trabajo, ahora sé exactamente cuándo un candidato me está mintiendo o exagerando. Me ha ahorrado contratar a dos personas que hubieran sido un problema." data-id="element-186" />
            <TestimonialCard name="Javier Morales" role="Emprendedor, 29 años" content="Lo mejor es que es práctico. Nada de teoría aburrida. Lo lees y al día siguiente ya estás notando cosas en la calle, en el transporte, en la oficina. 100% recomendado." data-id="element-187" />
            <TestimonialCard name="Sofía Mendoza" role="Abogada, 38 años" content="La sección de psicología oscura me abrió los ojos. Me di cuenta de cómo un colega me estaba manipulando sutilmente. Ahora tengo las herramientas para bloquearlo." data-id="element-188" />
            <TestimonialCard name="Diego Hernández" role="Estudiante universitario, 24 años" content="Siempre fui tímido y me costaba hablar en público. Las técnicas de proyección de autoridad cambiaron cómo me presento frente a los demás. Todos notaron el cambio." data-id="element-189" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-black py-12 px-4 border-t border-zinc-800 text-center text-gray-600 text-sm" data-id="element-190">
        <div className="max-w-4xl mx-auto" data-id="element-191">
          <p className="mb-4" data-id="element-192">
            &copy; {new Date().getFullYear()} Manual de Lenguaje Corporal. Todos
            los derechos reservados.
          </p>
          <div className="flex justify-center gap-6" data-id="element-193">
            <a href="#" className="hover:text-gray-400" data-id="element-194">
              Términos y Condiciones
            </a>
            <a href="#" className="hover:text-gray-400" data-id="element-195">
              Política de Privacidad
            </a>
            <a href="#" className="hover:text-gray-400" data-id="element-196">
              Contacto
            </a>
          </div>
          <p className="mt-8 text-xs text-gray-700" data-id="element-197">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio no está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>
    </div>;
}
// Helper Components for SalesPage
function CheckIcon({
  className
}: {
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-198">
      <polyline points="20 6 9 17 4 12" data-id="element-199"></polyline>
    </svg>;
}
function BenefitCard({
  icon,
  title,
  description
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return <div className="bg-zinc-900 p-8 rounded-xl border border-zinc-800 hover:border-yellow-500/50 transition-colors group" data-id="element-200">
      <div className="bg-black w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform border border-zinc-800 group-hover:border-yellow-500/30" data-id="element-201">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-white mb-3" data-id="element-202">{title}</h3>
      <p className="text-gray-400 leading-relaxed" data-id="element-203">{description}</p>
    </div>;
}
function BonusCard({
  image,
  title,
  subtitle,
  description
}: {
  image: string;
  title: string;
  subtitle: string;
  description: string;
}) {
  return <div className="bg-black rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-700 transition-all" data-id="element-204">
      <div className="h-72 overflow-hidden bg-zinc-900/50 relative flex items-center justify-center p-4" data-id="element-205">
        <img src={image} alt={title} className="max-w-full max-h-full object-contain opacity-90 hover:opacity-100 transition-opacity" data-id="element-206" />
      </div>
      <div className="p-6" data-id="element-207">
        <h3 className="text-yellow-400 font-bold text-xl mb-1" data-id="element-208">{title}</h3>
        <h4 className="text-white font-medium text-lg mb-3" data-id="element-209">{subtitle}</h4>
        <p className="text-gray-500 text-sm" data-id="element-210">{description}</p>
      </div>
    </div>;
}